import { IkeToken } from '@ikegps/core-api';
import type { JobLoadingSummaryResults } from '@ikegps/core-api';
import { observable, makeObservable } from 'mobx';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { PhotomeasureButton } from '../../modules/photomeasure.button/photomeasure.button';
import { Services } from '../../services';
import { IkePage, type IkePageConfig, IkePageName } from '../page';
import { DashBanner } from './dashboard.banner';
import { CSS } from './dashboard.style';
import { Table } from './dashboard.table';
import { CSS as TableCSS } from './dashboard.table.style';

type JobData = {
	load100: number;
	load75: number;
	load50: number;
	load0: number;
	loadNA: number;
	jobName: string;
	jobId: string;
};

export const DashPoleLoadingConfig: IkePageConfig = {
	type: 'internal',
	name: IkePageName.dashboardPoleloading,
	path: '/dashboard/pole_loading',
	auth: {
		required: true,
	},
	layout: { nav: false, banner: true },
};

export class DashPoleLoading extends IkePage {
	@observable poleLoadingSum: JobLoadingSummaryResults[] | null = null;

	constructor() {
		super(DashPoleLoadingConfig);
		makeObservable(this);
	}

	async init() {
		this.poleLoadingSum = await Services.Dashboard.getPoleLoadingSummary();
	}

	get nodes(): MithrilVNode {
		let nodes: MithrilVNode | null = null;

		if (!Services.Token.has(IkeToken.DASHBOARD_PAGE)) {
			nodes = m('div', { className: CSS.container }, m('div', { className: CSS.message }, 'No Dashboard Token'));
			return nodes;
		}

		if (this.poleLoadingSum == null) {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			this.init();
		}

		nodes = m('div', { className: CSS.container }, [DashBanner.nodes, this.viewTable()]);

		return nodes;
	}

	private getJobData(job: JobLoadingSummaryResults): JobData {
		let load100: number = 0;
		let load75: number = 0;
		let load50: number = 0;
		let load0: number = 0;
		let loadNA: number = 0;
		let jobName: string = job.jobName == null ? 'NULL' : job.jobName;
		let jobId: string = job.jobId == null ? '' : job.jobId;
		if (job.collectionLoadingSummary != null) {
			job.collectionLoadingSummary.forEach((pole) => {
				if (pole != null) {
					if (pole.loading == null) {
						loadNA++;
					} else {
						const loadString: string = pole.loading;
						const load: number = parseFloat(loadString.replace(/[^\d.-]/g, ''));
						if (load >= 100) {
							load100++;
						} else if (load >= 75 && load < 100) {
							load75++;
						} else if (load >= 50 && load < 75) {
							load50++;
						} else if (load >= 0 && load < 50) {
							load0++;
						} else {
							loadNA++;
						}
					}
				}
			});
		}
		return { load100, load75, load50, load0, loadNA, jobName, jobId };
	}

	private viewTable(): MithrilVNode {
		const headers = ['   #   ', 'JOB NAME', '100+%', '99-75%', '74-50%', '49-0%', 'OTHER', 'POLES TOTAL'];
		const rows: MithrilVNode[][] = [];

		let i: number = 0;
		let load100total: number = 0;
		let load75total: number = 0;
		let load50total: number = 0;
		let load0total: number = 0;
		let loadNAtotal: number = 0;

		if (this.poleLoadingSum != null) {
			const res = this.poleLoadingSum;
			res.forEach((tag) => {
				if (tag != null) {
					let job = this.getJobData(tag);
					i++;
					load100total += job.load100;
					load75total += job.load75;
					load50total += job.load50;
					load0total += job.load0;
					loadNAtotal += job.loadNA;
					rows.push(this.viewRow(i, job));
				}
			});
			//puts the totals at the top of the list
			rows.unshift(this.totalsRow(load100total, load75total, load50total, load0total, loadNAtotal));

			//only display if we get any pole load counts
			if (load100total + load75total + load50total + load0total + loadNAtotal > 0) {
				return Table.view(this.viewTitle(), headers, rows);
			}
		}

		return m('div', { className: CSS.message }, 'No Data Available');
	}

	private viewTitle(): MithrilVNode {
		return m('div', { className: TableCSS.title }, [m('h6', {}, 'POLE LOADING')]);
	}

	private totalsRow(load100, load75, load50, load0, loadNA) {
		const total = load100 + load75 + load50 + load0 + loadNA;

		const row = [
			m('span', { className: CSS.tableCell }, '-'),
			m('span', { className: CSS.tableCell }, 'Totals:'),
			m('span', { className: CSS.tableCell }, load100),
			m('span', { className: CSS.tableCell }, load75),
			m('span', { className: CSS.tableCell }, load50),
			m('span', { className: CSS.tableCell }, load0),
			m('span', { className: CSS.tableCell }, loadNA),
			m('span', { className: CSS.tableCell }, total),
		];

		return row;
	}
	private viewRow(index: number, job: JobData) {
		const total = job.load100 + job.load75 + job.load50 + job.load0 + job.loadNA;

		const row = [
			m('span', { className: CSS.tableCellFaint }, index),
			m('div', { className: CSS.tableCellRoleActions }, [
				m('div', { className: CSS.tableCell }, job.jobName),
				m('div', { className: CSS.tableCellGhost }, PhotomeasureButton.view(job.jobId)),
			]),
			m('span', { className: CSS.tableCellFaint }, job.load100),
			m('span', { className: CSS.tableCell }, job.load75),
			m('span', { className: CSS.tableCellFaint }, job.load50),
			m('span', { className: CSS.tableCell }, job.load0),
			m('span', { className: CSS.tableCellFaint }, job.loadNA),
			m('span', { className: CSS.tableCell }, total),
		];

		return row;
	}
}
